import {
  type ChildRef,
  type Club,
  type ClubMembership,
  type ClubSlot,
  type EventKind,
  WEEKDAY_SHORT,
  clubError,
  clubTimes,
  fitsGrade,
  seatsLeft,
  useClubActions,
  useClubMemberships,
  useClubRoster,
  useClubs,
  useTeachers,
} from '@shkola/api';
import { type SubjectIcon, subjectLook } from '@shkola/design';
import type { TFunction } from 'i18next';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext, useViewContext } from '../app/ViewContext';
import { Button, Modal, Spinner } from '../components/ui';
import {
  Block,
  EventArt,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  Tabs,
  grad,
  pillClass,
} from '../design/forms';
import { CheckIcon, ClockIcon, PlusIcon, UsersThreeIcon } from '../design/icons';

/*
 * «Гуртки» in «Форми 2» (28.10): the title on the paper with a line of what to do here and the choice
 * (for my class / mine / all) as tabs; every club a soft card in its colour with its picture big and
 * quiet behind (the robots — a computer, the football — a dumbbell, the chess — a cup), who leads it and
 * for which classes, when and where, the seats; at the bottom — to join, to leave, to answer a request.
 * A guardian sees the children's requests first; leaders and the administration see who is in.
 */

type Filter = 'fit' | 'mine' | 'all';

/** A club's picture: one of the subjects' or of the events', and its colour. */
type Look = { tone: Grad } & ({ subject: SubjectIcon } | { event: EventKind });

const BY_EMOJI: Record<string, Look> = {
  '🤖': { tone: 'aqua', subject: 'Monitor' },
  '🐍': { tone: 'aqua', subject: 'Monitor' },
  '💻': { tone: 'aqua', subject: 'Monitor' },
  '♟': { tone: 'sand', event: 'exam' },
  '🏆': { tone: 'sand', event: 'exam' },
  '🎭': { tone: 'pink', event: 'celebration' },
  '⚽': { tone: 'apricot', subject: 'Dumbbell' },
  '🏀': { tone: 'apricot', subject: 'Dumbbell' },
  '🏐': { tone: 'apricot', subject: 'Dumbbell' },
  '🤸': { tone: 'apricot', subject: 'Dumbbell' },
  '🎨': { tone: 'pink', subject: 'Palette' },
  '🎵': { tone: 'lavender', subject: 'Music' },
  '🎶': { tone: 'lavender', subject: 'Music' },
  '🎤': { tone: 'lavender', subject: 'Music' },
  '🌱': { tone: 'mint', subject: 'Sprout' },
  '🌿': { tone: 'mint', subject: 'Sprout' },
  '🗣': { tone: 'coral', subject: 'Languages' },
  '📰': { tone: 'butter', subject: 'PenLine' },
  '🧩': { tone: 'cobalt', event: 'meeting' },
  '🔬': { tone: 'aqua', subject: 'FlaskConical' },
  '🧪': { tone: 'aqua', subject: 'FlaskConical' },
  '📚': { tone: 'pink', subject: 'BookOpen' },
  '📖': { tone: 'pink', subject: 'BookOpen' },
  '🚀': { tone: 'lavender', subject: 'Telescope' },
};
const FALLBACK: ReadonlyArray<Grad> = ['lavender', 'sky', 'mint', 'apricot', 'butter', 'pink'];

function lookOf(club: Club): Look {
  const byEmoji = club.emoji && BY_EMOJI[club.emoji.replace(/️/g, '')];
  if (byEmoji) return byEmoji;
  const subject = subjectLook(club.title);
  if (subject.icon !== 'BookMarked')
    return { tone: SUBJECT_GRAD[subject.tone], subject: subject.icon };
  let hash = 0;
  for (const ch of club.title) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return { tone: FALLBACK[hash % FALLBACK.length] as Grad, event: 'other' };
}

function ClubArt({
  look,
  size,
  ghost,
  className,
}: {
  look: Look;
  size: number;
  ghost?: boolean;
  className?: string;
}) {
  return 'subject' in look ? (
    <SubjectArt
      icon={look.subject}
      tone={look.tone}
      size={size}
      ghost={ghost}
      className={className}
    />
  ) : (
    <EventArt kind={look.event} tone={look.tone} size={size} ghost={ghost} className={className} />
  );
}

/** A label: a soft rectangle, white on a coloured card. */
function Tag({ tone, children }: { tone: Grad; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={{ backgroundColor: 'rgb(255 255 255 / 0.78)', color: GRAD[tone].ink }}
    >
      {children}
    </span>
  );
}

/** The words of a key without the old ✓ and ＋ marks: the icons say it now. */
const bare = (s: string) => s.replace(/\s*[✓＋+]\s*/g, ' ').trim();

function grades(t: TFunction, c: Club) {
  return c.gradeFrom === c.gradeTo
    ? t('clubs.gradesOne', { n: c.gradeFrom })
    : t('clubs.grades', { from: c.gradeFrom, to: c.gradeTo });
}

function Roster({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const roster = useClubRoster(clubId);
  if (roster.isPending) return <Spinner />;
  const rows = roster.data ?? [];
  if (rows.length === 0)
    return (
      <p className="text-[14px]" style={{ color: INK_SOFT }}>
        {t('clubs.rosterEmpty')}
      </p>
    );
  return (
    <ol className="flex flex-col gap-1 text-[14px]">
      {rows.map((r, i) => (
        <li key={r.studentId} className="flex gap-2">
          <span className="w-5 text-right" style={{ color: INK_FAINT }}>
            {i + 1}
          </span>
          <span className="min-w-0 flex-1" style={{ color: INK }}>
            {r.name}
            {r.classLabel && <span style={{ color: INK_SOFT }}> · {r.classLabel}</span>}
          </span>
          {r.status === 'pending' && (
            <span className="text-[12px]" style={{ color: GRAD.butter.ink }}>
              {t('clubs.waiting')}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}

/** What the person can do with a club: join, enrol a child, answer a request, see who is in. */
function ClubActions({
  club,
  memberships,
}: {
  club: Club;
  memberships: readonly ClubMembership[];
}) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = useClubActions(ctx.schoolId);
  const [error, setError] = useState<string>();
  const [roster, setRoster] = useState(false);
  const [sure, setSure] = useState(false);
  const run = (p: Promise<unknown>) => {
    setError(undefined);
    p.catch((e: unknown) => setError(t(`clubs.errors.${clubError(e)}`)));
  };
  const left = seatsLeft(club);
  const failed = error && (
    <p role="alert" className="text-[13px] font-medium" style={{ color: '#D9471A' }}>
      {error}
    </p>
  );

  if (ctx.kind === 'student' || ctx.kind === 'guardian') {
    const student = ctx.child.memberId;
    const m = memberships.find((x) => x.clubId === club.id && x.studentId === student);
    const a = { clubId: club.id, studentId: student };
    const name = ctx.child.firstName;
    const self = ctx.kind === 'student';
    const busy = actions.join.isPending || actions.leave.isPending;
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {!m && (
            <button
              type="button"
              disabled={busy || left === 0 || !fitsGrade(club, ctx.child.class?.grade)}
              onClick={() => run(actions.join.mutateAsync(a))}
              className={`${pillClass('dark')} disabled:opacity-35`}
            >
              {self ? t('clubs.join') : t('clubs.enrol', { name })}
              <PillIcon icon={PlusIcon} />
            </button>
          )}
          {m?.status === 'active' && (
            <>
              <Tag tone="mint">
                <CheckIcon size={12} weight="bold" aria-hidden />
                {bare(self ? t('clubs.joined') : t('clubs.enrolled', { name }))}
              </Tag>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(actions.leave.mutateAsync(a))}
                className={`${pillClass('light', 'sm')} ml-auto pr-3.5 disabled:opacity-50`}
              >
                {self ? t('clubs.leave') : t('clubs.unenrol')}
              </button>
            </>
          )}
          {m?.status === 'pending' &&
            (self ? (
              <>
                <Tag tone="butter">
                  <ClockIcon size={12} weight="fill" aria-hidden />
                  {t('clubs.pending')}
                </Tag>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(actions.leave.mutateAsync(a))}
                  className={`${pillClass('light', 'sm')} ml-auto pr-3.5 disabled:opacity-50`}
                >
                  {t('clubs.cancel')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(actions.join.mutateAsync(a))}
                  className={`${pillClass('dark')} disabled:opacity-35`}
                >
                  {t('clubs.approve')}
                  <PillIcon icon={CheckIcon} />
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(actions.leave.mutateAsync(a))}
                  className={`${pillClass('light')} pr-4.5 disabled:opacity-50`}
                >
                  {t('clubs.decline')}
                </button>
              </>
            ))}
        </div>
        {failed}
      </div>
    );
  }

  const admin = ctx.kind === 'admin';
  const leads = club.leaderId === ctx.memberId;
  if (!admin && !leads) return null;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={() => setRoster(true)}
          className={pillClass('light', 'sm')}
        >
          {t('clubs.roster')}
          <span style={{ color: INK_SOFT }}>{club.taken}</span>
          <PillIcon icon={UsersThreeIcon} size="sm" />
        </button>
        {admin && (
          <button
            type="button"
            disabled={actions.remove.isPending}
            onClick={() => (sure ? run(actions.remove.mutateAsync(club.id)) : setSure(true))}
            className={`${sure ? 'inline-flex h-9 items-center rounded-full px-3.5 text-[13px] font-medium' : `${pillClass('light', 'sm')} pr-3.5`} ml-auto disabled:opacity-50`}
            style={sure ? { backgroundImage: grad('coral'), color: GRAD.coral.ink } : undefined}
          >
            {sure ? t('clubs.removeSure') : t('clubs.remove')}
          </button>
        )}
      </div>
      {roster && (
        <Modal title={`${t('clubs.roster')} · ${club.title}`} onClose={() => setRoster(false)}>
          <Roster clubId={club.id} />
        </Modal>
      )}
      {failed}
    </div>
  );
}

function ClubCard({ club, memberships }: { club: Club; memberships: readonly ClubMembership[] }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const left = seatsLeft(club);
  const look = lookOf(club);
  const tone = look.tone;
  return (
    <article
      className="relative isolate flex min-h-[300px] min-w-0 flex-col overflow-hidden rounded-[28px] p-5"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <ClubArt
        look={look}
        size={150}
        ghost
        className="pointer-events-none absolute -top-4 -right-5 -z-10"
      />
      <div className="flex flex-wrap gap-1.5 pr-14">
        <Tag tone={tone}>{grades(t, club)}</Tag>
        {club.leaderId === ctx.memberId && <Tag tone="lavender">{t('clubs.leads')}</Tag>}
      </div>
      <h2
        className="mt-5 font-display text-[26px] leading-[32px] font-light tracking-[-0.03em]"
        style={{ color: INK }}
      >
        {club.title}
      </h2>
      {club.leader && <p className="mt-0.5 text-[13px] leading-[18px]">{club.leader}</p>}
      {club.about && (
        <p className="mt-2.5 text-[14.5px] leading-[21px]" style={{ color: INK }}>
          {club.about}
        </p>
      )}
      <p className="mt-3 flex items-start gap-1.5 text-[13.5px] leading-5" style={{ color: INK }}>
        <ClockIcon size={14} weight="fill" aria-hidden className="mt-[3px] shrink-0" />
        <span>
          {clubTimes(club.slots)}
          {club.room && <span style={{ color: INK_SOFT }}> · {club.room}</span>}
        </span>
      </p>
      <p
        className="mt-1 text-[13px] font-medium"
        style={left === 0 ? { color: '#D9471A' } : undefined}
      >
        {left === null
          ? t('clubs.anyone')
          : left === 0
            ? t('clubs.noSeats')
            : t('clubs.seats', { count: left })}
      </p>
      <div className="mt-auto pt-4">
        <ClubActions club={club} memberships={memberships} />
      </div>
    </article>
  );
}

/** A guardian answers the requests of children in primary school. */
function Requests({
  clubs,
  memberships,
  children,
}: {
  clubs: readonly Club[];
  memberships: readonly ClubMembership[];
  children: readonly ChildRef[];
}) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = useClubActions(ctx.schoolId);
  const pending = memberships.filter((m) => m.status === 'pending');
  if (pending.length === 0) return null;
  return (
    <section
      className="relative isolate flex flex-col gap-3 overflow-hidden rounded-[28px] p-5 md:p-6"
      style={{ backgroundImage: grad('pink'), color: GRAD.pink.ink }}
    >
      <TaskArt
        kind="ack"
        tone="pink"
        size={160}
        ghost
        className="pointer-events-none absolute -top-5 -right-5 -z-10"
      />
      <h2
        className="font-display text-[28px] leading-[34px] font-light tracking-[-0.03em]"
        style={{ color: INK }}
      >
        {t('clubs.requests')}
      </h2>
      {pending.map((m) => {
        const club = clubs.find((c) => c.id === m.clubId);
        const child = children.find((c) => c.memberId === m.studentId);
        if (!club || !child) return null;
        const a = { clubId: m.clubId, studentId: m.studentId };
        return (
          <div
            key={`${m.clubId}-${m.studentId}`}
            className="flex flex-wrap items-center gap-3 rounded-[20px] bg-white/90 p-3 pl-4"
          >
            <ClubArt look={lookOf(club)} size={40} />
            <p className="min-w-0 flex-1 text-[15px] leading-5" style={{ color: INK }}>
              {t('clubs.request', { name: child.firstName, club: club.title })}
              <span className="block text-[13px]" style={{ color: INK_SOFT }}>
                {clubTimes(club.slots)}
              </span>
            </p>
            <button
              type="button"
              disabled={actions.join.isPending}
              onClick={() => actions.join.mutate(a)}
              className={`${pillClass('dark')} disabled:opacity-35`}
            >
              {t('clubs.approve')}
              <PillIcon icon={CheckIcon} />
            </button>
            <button
              type="button"
              disabled={actions.leave.isPending}
              onClick={() => actions.leave.mutate(a)}
              className={`${pillClass('light')} pr-4.5 disabled:opacity-50`}
            >
              {t('clubs.decline')}
            </button>
          </div>
        );
      })}
    </section>
  );
}

const EMPTY_SLOT: ClubSlot = { weekday: 2, startsAt: '15:00', endsAt: '16:00' };

/** The administration opens a new club. */
function NewClub({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const teachers = useTeachers(ctx.schoolId);
  const actions = useClubActions(ctx.schoolId);
  const [title, setTitle] = useState('');
  const [emoji, setEmoji] = useState('⭐');
  const [about, setAbout] = useState('');
  const [leaderId, setLeaderId] = useState('');
  const [from, setFrom] = useState(5);
  const [to, setTo] = useState(11);
  const [capacity, setCapacity] = useState('15');
  const [room, setRoom] = useState('');
  const [slots, setSlots] = useState<ClubSlot[]>([EMPTY_SLOT]);
  const field =
    'w-full rounded-[14px] bg-white px-3.5 py-2.5 text-[15px] text-[var(--sh-ink)] outline-none ring-1 ring-[#E4E0D7] ring-inset focus:ring-2 focus:ring-[var(--sh-dark)]';
  const label = 'mb-1.5 block text-[13px] font-medium text-[var(--sh-ink-soft)]';
  const setSlot = (i: number, patch: Partial<ClubSlot>) =>
    setSlots(slots.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  return (
    <Modal title={t('clubs.form.button')} onClose={onClose}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void actions.create
            .mutateAsync({
              title,
              emoji,
              about,
              leaderId,
              gradeFrom: Math.min(from, to),
              gradeTo: Math.max(from, to),
              capacity: capacity.trim() === '' ? null : Number(capacity),
              room,
              slots,
            })
            .then(onClose);
        }}
      >
        <div className="grid grid-cols-[80px_1fr] gap-2">
          <label>
            <span className={label}>{t('clubs.form.emoji')}</span>
            <input
              value={emoji}
              maxLength={4}
              onChange={(e) => setEmoji(e.target.value)}
              className={field}
            />
          </label>
          <label>
            <span className={label}>{t('clubs.form.name')}</span>
            <input
              required
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              className={field}
            />
          </label>
        </div>
        <label className="block">
          <span className={label}>{t('clubs.form.about')}</span>
          <textarea
            rows={2}
            value={about}
            maxLength={1000}
            onChange={(e) => setAbout(e.target.value)}
            className={field}
          />
        </label>
        <label className="block">
          <span className={label}>{t('clubs.form.leader')}</span>
          <select value={leaderId} onChange={(e) => setLeaderId(e.target.value)} className={field}>
            <option value="">{t('clubs.form.noLeader')}</option>
            {(teachers.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.last_name} {p.first_name} {p.middle_name ?? ''}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-3 gap-2">
          <label>
            <span className={label}>{t('clubs.form.gradeFrom')}</span>
            <input
              type="number"
              min={1}
              max={11}
              value={from}
              onChange={(e) => setFrom(Number(e.target.value))}
              className={field}
            />
          </label>
          <label>
            <span className={label}>{t('clubs.form.gradeTo')}</span>
            <input
              type="number"
              min={1}
              max={11}
              value={to}
              onChange={(e) => setTo(Number(e.target.value))}
              className={field}
            />
          </label>
          <label>
            <span className={label}>{t('clubs.form.room')}</span>
            <input
              value={room}
              maxLength={60}
              onChange={(e) => setRoom(e.target.value)}
              className={field}
            />
          </label>
        </div>
        <label className="block">
          <span className={label}>{t('clubs.form.capacity')}</span>
          <input
            type="number"
            min={1}
            max={500}
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            className={field}
          />
        </label>
        {slots.map((s, i) => (
          <div key={i} className="grid grid-cols-3 gap-2">
            <label>
              <span className={label}>{t('clubs.form.day')}</span>
              <select
                value={s.weekday}
                onChange={(e) => setSlot(i, { weekday: Number(e.target.value) })}
                className={field}
              >
                {[1, 2, 3, 4, 5, 6].map((d) => (
                  <option key={d} value={d}>
                    {WEEKDAY_SHORT[d]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className={label}>{t('clubs.form.start')}</span>
              <input
                type="time"
                value={s.startsAt}
                onChange={(e) => setSlot(i, { startsAt: e.target.value })}
                className={field}
              />
            </label>
            <label>
              <span className={label}>{t('clubs.form.end')}</span>
              <input
                type="time"
                value={s.endsAt}
                onChange={(e) => setSlot(i, { endsAt: e.target.value })}
                className={field}
              />
            </label>
          </div>
        ))}
        {slots.length < 3 && (
          <div>
            <button
              type="button"
              onClick={() => setSlots([...slots, EMPTY_SLOT])}
              className={pillClass('light', 'sm')}
            >
              {bare(t('clubs.form.addSlot'))}
              <PillIcon icon={PlusIcon} size="sm" />
            </button>
          </div>
        )}
        <Button type="submit" className="mt-1" disabled={actions.create.isPending || !title.trim()}>
          {t('clubs.form.save')}
        </Button>
      </form>
    </Modal>
  );
}

/**
 * «Гуртки»: the clubs of the school. Students join in one tap (primary school asks a guardian),
 * guardians enrol and answer requests, leaders and the administration see who is in.
 */
export function ClubsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const { contexts } = useViewContext();
  const clubs = useClubs(ctx.schoolId);
  const family = ctx.kind === 'student' || ctx.kind === 'guardian';
  // a guardian answers for every child at this school
  const children: ChildRef[] =
    ctx.kind === 'student'
      ? [ctx.child]
      : contexts.flatMap((c) =>
          c.kind === 'guardian' && c.schoolId === ctx.schoolId ? [c.child] : [],
        );
  const memberships = useClubMemberships(children.map((c) => c.memberId)).data ?? [];
  const grade = family ? ctx.child.class?.grade : undefined;
  const [filter, setFilter] = useState<Filter>(family ? 'fit' : 'all');
  const [creating, setCreating] = useState(false);
  const all = clubs.data ?? [];
  const mineIds = new Set(
    family
      ? memberships.filter((m) => m.studentId === ctx.child.memberId).map((m) => m.clubId)
      : all.filter((c) => c.leaderId === ctx.memberId).map((c) => c.id),
  );
  const shown = all.filter((c) =>
    filter === 'fit' ? fitsGrade(c, grade) : filter === 'mine' ? mineIds.has(c.id) : true,
  );
  const filters: Filter[] = family
    ? ['fit', 'mine', 'all']
    : mineIds.size > 0
      ? ['all', 'mine']
      : [];

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('clubs.title')}
        sub={
          ctx.kind === 'student'
            ? t('clubs.intro')
            : ctx.kind === 'guardian'
              ? t('clubs.introFamily')
              : t('clubs.introStaff')
        }
      >
        {filters.length > 0 && (
          <span className="hidden lg:block">
            <Tabs
              label={t('clubs.title')}
              value={filter}
              onChange={setFilter}
              options={filters.map((f) => ({
                id: f,
                label: f === 'fit' ? t('clubs.forClass', { n: grade ?? '' }) : t(`clubs.${f}`),
              }))}
            />
          </span>
        )}
        {ctx.kind === 'admin' && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className={`${pillClass('dark')} max-lg:w-11 max-lg:justify-center max-lg:px-0`}
          >
            <span className="max-lg:sr-only">{t('clubs.form.button')}</span>
            <PillIcon icon={PlusIcon} />
          </button>
        )}
      </PageHead>
      {filters.length > 0 && (
        <div className="-mt-1 flex overflow-x-auto lg:hidden">
          <Tabs
            label={t('clubs.title')}
            value={filter}
            onChange={setFilter}
            options={filters.map((f) => ({
              id: f,
              label: f === 'fit' ? t('clubs.forClass', { n: grade ?? '' }) : t(`clubs.${f}`),
            }))}
          />
        </div>
      )}

      {ctx.kind === 'guardian' && (
        <Requests clubs={all} memberships={memberships} children={children} />
      )}

      {clubs.isPending ? (
        <Block>
          <Spinner />
        </Block>
      ) : shown.length === 0 ? (
        <Block className="items-center py-14 text-center">
          <EventArt kind="other" tone="lavender" size={96} />
          <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
            {filter === 'mine' && ctx.kind === 'student' ? t('clubs.emptyMine') : t('clubs.empty')}
          </p>
        </Block>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((c) => (
            <ClubCard key={c.id} club={c} memberships={memberships} />
          ))}
        </div>
      )}
      {creating && <NewClub onClose={() => setCreating(false)} />}
    </div>
  );
}
